<template>
  <div>
    <link rel="stylesheet" href="https://use.fontawesome.com/releases/v5.8.1/css/all.css" integrity="sha384-50oBUHEmvpQ+1lW4y57PTFmhCaXp0ML5d60M1M7uH2+nqUivzIebhndOJK28anvf" crossorigin="anonymous">
    <div class="card" v-if="project">
      <div class="card__image">
        <div class="card__image__inside">
          <img :src="project.image" :alt="project.name" class="w-100" />
        </div>
      </div>
      <h1 class="card__title d-flex">
        <div class="full-width">{{ project.name }}</div>
        <div v-if="loggedIn && (project.organisator || isModerator || isAdmin)">
          <b-button v-if="!editing" v-b-tooltip.hover :title="$t('messages.manage')" variant="primary" @click="$emit('startEditing', true)"
                    :disabled="project.status === 'CANCELLED'">
            <font-awesome-icon icon="pencil-alt"></font-awesome-icon>
          </b-button>
          <b-button v-if="editing" v-b-tooltip.hover :title="$t('messages.back_to_project')" variant="primary" @click="$emit('startEditing', false)">
            <font-awesome-icon icon="arrow-left"></font-awesome-icon>
          </b-button>
        </div>
      </h1>
      <div class="card__started__by d-flex">
        <p>{{ $t('messages.started_by') }} <b-button v-if="project.store" v-b-tooltip.hover :title="$t('messages.store_account_info')" class="ml-1 mr-1 noPadding noBackground secondary"><font-awesome-icon icon="store-alt"></font-awesome-icon></b-button> <b-link :to="{ path: '/user/' + slugText(project.user_id + '-' + project.user_full_name) }">{{ project.user_full_name }}</b-link></p>
      </div>
      <div class="card__description border-0 pb-2">
        <div class="mb-3" v-if="editing">
          <b-form-checkbox v-model="project.private" class="privateCheckbox">{{ $t('messages.private') }}</b-form-checkbox>
        </div>
        <p v-if="!editing" v-html="project.description"></p>
        <ckeditor v-if="editing" id="projectContent" name="projectContent" :editor="editor.instance" v-model="editor.data"
                  :config="editor.config" :key="editor.update"
                  @input="$v.editor.data.$touch"></ckeditor>
      </div>
      <div class="pl-20 pr-20 pt-3 pb-2">
        <div class="box-icon d-flex">
          <div class="box-icon-wrapper" :class="editing ? 'calendar-position-fix' : ''">
            <font-awesome-icon class="green" icon="calendar-alt"></font-awesome-icon>
          </div>
          <span v-if="!editing" class="month-position-fix strong green">{{ translateMonth(project.expected_date) }}</span>
          <span v-if="editing" class="d-flex ml-1">
                <date-picker id="expectedDatePicker" name="expectedDatePicker"
                             v-model="expectedDate" :config="expectedDateOptions" class="month-date-picker-width"
                             @input="$v.expectedDate.$touch"></date-picker>
                <b-form-checkbox v-model="sendUpdate" class="ml-3 mr-3 updateCheckbox">{{ $t('messages.send_update') }}</b-form-checkbox>
              </span>
        </div>
      </div>
      <div class="mt-3"></div>
      <div class="card__url bg-primary"><a :href="project.url" target="_blank">{{ $t('messages.check_project') }} {{ $t('codes.' + project.source.toLowerCase()) }}</a></div>
      <div class="card__bottom">
        <div class="d-flex">
          <span class="mr-1" :class="editing ? 'd-flex flex-wrap align-content-center' : ''">{{ $t('messages.deadline') }}: </span>
          <span v-if="!editing"><countdown :key="countdown.update" :expiredCallback="setDeadline" :date="project.deadline_date"></countdown></span>
          <span v-if="editing" class="full-width ml-2">
                <date-picker v-model="deadlineDate" :config="deadlineDateOptions" class="deadline-date-picker-width mr-2"
                             @input="$v.deadlineDate.$touch"></date-picker>
              </span>
        </div>
      </div>
      <div class="card__bottom d-flex justify-content-between pt-0" v-if="editing">
        <b-form @submit.prevent="saveProject">
          <notification-button :message="$t('messages.edit')" :notification="notifications.project" field="project"
                               :disabled="$v.$invalid"></notification-button>
        </b-form>
        <b-form @submit.prevent="cancelProject">
          <click-confirm button-size="sm" :messages="confirmMessage" v-if="totalParticipantsPaid === 0">
            <notification-button v-b-tooltip.hover :title="$t('messages.cancel_project_text')" :message="$t('messages.cancel_project')"
                                 :notification="notifications.cancel" field="cancel"></notification-button>
          </click-confirm>
        </b-form>
        <notification-button v-b-tooltip.hover :title="$t('messages.cancel_project_text')" :message="$t('messages.cancel_project')"
                             :disabled="true" v-if="totalParticipantsPaid > 0" field="cancel"></notification-button>
      </div>
    </div>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import DatePicker from 'vue-bootstrap-datetimepicker'
import Countdown from './Countdown'

import { projectService } from '../../services/project.service'
import NotificationButton from '../NotificationButton'

import { defaultMixin } from '../../mixin/default'
import { notificationMixin } from '../../mixin/notification'
import { validationMixin } from 'vuelidate'

import { required } from 'vuelidate/lib/validators'

import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css'

export default {
  name: 'projectinfo',
  mixins: [ defaultMixin, notificationMixin, validationMixin ],
  components: { NotificationButton, DatePicker, Countdown },
  props: {
    value: Object,
    editing: Boolean,
    totalParticipantsPaid: Number
  },
  data: function () {
    return {
      project: this.value,
      editor: {
        instance: ClassicEditor,
        data: '',
        config: {
          toolbar: [ 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo' ],
          language: this.$i18n.locale
        },
        update: 0
      },
      expectedDate: null,
      expectedDateOptions: {
        format: 'MM-YYYY',
        locale: this.$i18n.locale
      },
      sendUpdate: true,
      deadlineDate: null,
      deadlineDateOptions: {
        format: 'YYYY-MM-DD HH:mm:ss',
        locale: this.$i18n.locale
      },
      countdown: {
        timer: null,
        update: 0
      },
      notifications: {
        project: null,
        cancel: null
      }
    }
  },
  validations: function () {
    return {
      editor: {
        data: {
          required
        }
      },
      expectedDate: {
        required
      },
      deadlineDate: {
        required
      }
    }
  },
  methods: {
    saveProject: function () {
      let sendUpdate = this.sendUpdate && this.expectedDate !== this.project.expected_date
      projectService.updateProject(this.project.id,
        { 'private': this.project.private,
          'description': this.editor.data,
          'expectedDate': this.expectedDate,
          'sendUpdate': sendUpdate,
          'deadlineDate': this.deadlineDate })
        .then(() => {
          this.project.description = this.editor.data
          this.project.expected_date = this.expectedDate
          this.project.deadline_date = this.deadlineDate
          this.setDeadline(false)
          this.removeCountdownTimer()
          this.setCountdownTimer()

          this.$store.commit('success', 'success.project_edit')
          this.sendNotification('project', 'success')
          if (sendUpdate) {
            this.$emit('refreshUpdates')
          }
        }, () => {
          this.$store.commit('error', 'errors.project_edit')
          this.sendNotification('project', 'error')
        })
    },
    cancelProject: function () {
      projectService.cancelProject(this.project.id)
        .then(() => {
          this.$emit('startEditing', false)
          this.project.status = 'CANCELLED'
          this.$store.commit('warning', 'errors.project_cancelled')
          this.sendNotification('cancel', 'success')
        }, () => {
          this.$store.commit('error', 'errors.cancel_project')
          this.sendNotification('cancel', 'error')
        })
    },
    setDeadline: function (deadline) {
      if (deadline) {
        this.removeCountdownTimer()
      }
      this.$emit('updateDeadline', deadline)
    },
    setCountdownTimer: function () {
      this.countdown.timer = setInterval(this.updateCountdownTimer, 1000)
    },
    removeCountdownTimer: function () {
      clearInterval(this.countdown.timer)
    },
    updateCountdownTimer: function () {
      this.countdown.update += 1
    }
  },
  created: function () {
    let self = this

    this.editor.data = this.project.description
    this.expectedDate = this.project.expected_date
    this.deadlineDate = this.project.deadline_date

    this.setCountdownTimer()
    this.$root.$on('updateLocale', () => {
      self.expectedDateOptions.locale = self.$i18n.locale
      self.deadlineDateOptions.locale = self.$i18n.locale
      self.editor.config.language = self.$i18n.locale
      self.editor.update += 1
    })
    this.$root.$on('resetNotification', (notification) => {
      self.resetNotification(notification)
    })
  },
  beforeDestroy: function () {
    this.removeCountdownTimer()
    this.$root.$off('updateLocale')
    this.$root.$off('resetNotification')
  }
}
</script>

<style scoped>
  .card__started__by {
    margin-top: -5px;
    padding: 0px 20px;
    height: 35px;
  }

  .card__url {
    padding: 20px;
    border-bottom: 1px #D3D3D3 solid;
  }

  .card__url a {
    color: #fff;
    font-weight: bold;
  }

  .calendar-position-fix {
    padding-top: 5px;
  }

  .month-position-fix {
    padding-left: 5px;
    padding-top: 1px;
  }

  .month-date-picker-width {
    width: 150px;
  }

  .deadline-date-picker-width {
    width: 175px;
  }

  .updateCheckbox {
    padding-top: 6px;
    height: 100%;
  }
</style>



// WEBPACK FOOTER //
// src/components/project/ProjectInfo.vue